/*
 * PeterCox session-replay recorder (rrweb).
 *
 *   Records the page-load as an rrweb event stream and POSTs batches to
 *   petercox.ie/apps/events/replay/collect — the existing petercox-site-events
 *   App Proxy (subpath=events), forwarded to the session-replay-collector
 *   service on titan (project: cairn). Design: PM cairn/session-replay-design.
 *
 *   Identity: reads the SAME pcx_v / pcx_s cookies as pcx-tracker.js — the
 *   replay session_id IS the cairn session_id. The tracker (loaded first,
 *   both deferred) creates and slides them; this script only reads. If they
 *   are missing the recorder bails — never mints its own identity scheme.
 *
 *   Bot gate — record-on-interaction: rrweb records into an in-memory buffer
 *   from load, but nothing is transmitted until a human-shaped signal:
 *   pointer move >30px, scroll, click/pointerdown, keypress, or a second
 *   page-load in the same pcx_s session (localStorage-scoped — the pcx_s
 *   session spans tabs, so a product opened in a new tab still counts).
 *   No signal before unload → the buffer is discarded, nothing is ever sent
 *   (kills the single-page bot population at source; server-side
 *   classification stays flag-not-drop). The pre-gate buffer is capped: if
 *   it fills with no human signal the recording disarms whole (a replay
 *   missing its FullSnapshot is unreplayable, so partial drops are never safe).
 *
 *   Lifecycle: `pagehide` is the ONLY terminal signal — it drains everything
 *   and marks the last batch `final:true` (or sends a marker-only batch
 *   carrying a type-5 rrweb custom event when nothing is pending, so an
 *   idle-then-leave session still completes). The terminal marker must ride
 *   a keepalive POST — a plain fetch issued during pagehide is cancellable
 *   with the document — so the keepalive budget is granted FINAL-FIRST at
 *   the terminal drain, and a final data chunk too big for the budget is
 *   demoted in favour of a marker-only final batch (~300 B) that fits.
 *   `visibilitychange → hidden` is NOT terminal: tab switches hurry pending
 *   batches out with keepalive but recording continues and no final marker
 *   is sent. After the final marker, nothing further is ever transmitted
 *   (finalSent latch); a bfcache restore after pagehide does not resume the
 *   recording (accepted for v1 — one recording ends at the first real leave).
 *
 *   Privacy (final, Peter sign-off 2026-05-21): maskAllInputs globally,
 *   password + newsletter email inputs blocked outright (the theme's custom
 *   Klaviyo-backed forms: .pc-inline-nl__input, .pc-nl-input,
 *   .footer__newsletter-input — the footer input is server-side pre-filled
 *   with the logged-in customer's email, so block-level exclusion matters),
 *   recordCanvas off (3D covered by cairn's model_viewer_interacted),
 *   checkout never recorded (off-theme — this file never loads there),
 *   customer-account pages never recorded (PII renders there as plain DOM
 *   text, outside maskAllInputs' reach; structural exclusion lives in
 *   snippets/custom-replay-recorder.liquid — page_type guard, locale-proof).
 *
 *   Isolation: fully separate IIFE from pcx-tracker.js. Any failure here
 *   (including the vendored pcx-rrweb-record.min.js missing) must never
 *   break tracking — everything is guarded.
 */
(function () {
  'use strict';

  var COLLECT_URL = '/apps/events/replay/collect';
  var VISITOR_COOKIE = 'pcx_v';
  var SESSION_COOKIE = 'pcx_s';
  var SEEN_KEY = 'pcx_replay_seen';        // localStorage: 2nd-page-load gate (per pcx_s session, cross-tab)
  var BATCH_MAX_EVENTS = 50;               // flush threshold (collector caps 200 — 4x headroom)
  var BATCH_INTERVAL_MS = 5000;            // flush cadence once transmitting
  var MOVE_GATE_PX = 30;                   // pointer displacement gate
  var MAX_CHUNKS_PER_FLUSH = 6;            // pacing: max synchronous POSTs per non-final flush

  // Byte ceiling per batch, coordinated with the collector's reject-never-
  // truncate body cap (REPLAY_MAX_REQUEST_BYTES = 8 MiB,
  // session-replay-collector/service.py). 4 MiB = 2x headroom under that cap.
  // A single event larger than this (a FullSnapshot) ships alone in its own
  // batch — the measured heaviest-PDP snapshot is 1,708,219 B, ~4.7x under
  // the collector cap, so even the worst single event clears ingestion.
  var BATCH_MAX_BYTES = 4 * 1024 * 1024;

  // Pre-gate buffer cap (events). Harness-observed pre-gate buffers are tens
  // of events (signal arrives in seconds); 4000 is >100x headroom. Exceeding
  // it with no human signal means an idle tab with continuous DOM mutation
  // (carousel autoplay) — disarm rather than grow memory unboundedly.
  var PRE_GATE_BUFFER_MAX_EVENTS = 4000;

  // fetch keepalive bodies share a ~64 KiB per-origin in-flight quota counted
  // in BYTES (not UTF-16 chars), cumulative across concurrent keepalive
  // requests. Budget 48 KiB and account per-request (reserve on send, return
  // on settle), leaving 16 KiB headroom for other keepalive users on the page.
  var KEEPALIVE_QUOTA_BYTES = 49152;

  /* ─── PURE HELPERS BEGIN (unit-tested in tests/pcx-replay-recorder.test.mjs) ─── */

  function cookieFromString(cookieStr, name) {
    var prefix = name + '=';
    var parts = cookieStr ? cookieStr.split('; ') : [];
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].indexOf(prefix) === 0) return parts[i].slice(prefix.length);
    }
    return null;
  }

  // Squared-distance pointer gate: true once displacement strictly exceeds gatePx.
  function moveExceedsGate(x0, y0, x1, y1, gatePx) {
    var dx = x1 - x0;
    var dy = y1 - y0;
    return dx * dx + dy * dy > gatePx * gatePx;
  }

  // UTF-8 byte length. Both the fetch keepalive quota and the collector body
  // cap are byte quotas; String.length counts UTF-16 code units ('€' is
  // 1 char / 3 bytes), so char counts under-measure multibyte payloads.
  function utf8ByteLength(str) {
    if (typeof TextEncoder !== 'undefined') {
      return new TextEncoder().encode(str).length;
    }
    return str.length * 3; // no TextEncoder: worst-case overestimate (fail-safe)
  }

  function scrollDepthPct(scrollTop, winHeight, docHeight) {
    if (!docHeight || docHeight <= 0) return 0;
    return Math.min(100, Math.round(((scrollTop + winHeight) / docHeight) * 100));
  }

  // Terminal-marker vehicle for an idle final flush: the collector requires
  // >=1 event per batch (each an object with an int `type`), so a final flush
  // with empty pending rides a type-5 rrweb custom event (the addCustomEvent
  // envelope shape) — inert in replay, valid at ingestion.
  function makeFinalMarkerEvent(nowMs) {
    return { type: 5, data: { tag: 'pcx-replay-final' }, timestamp: nowMs };
  }

  // An empty (idle-final or demoted-final) chunk cannot post events: [] — the
  // collector rejects empty-events batches (min_length=1) — so the terminal
  // marker event is substituted in. Non-empty chunks pass through untouched.
  function substituteMarkerEvents(events, markerEvent) {
    return events.length === 0 ? [markerEvent] : events;
  }

  // Second-load gate comparison: a stored pcx_replay_seen value equal to the
  // CURRENT pcx_s session id means a prior page-load already ran in this
  // session (multi-page navigation is itself the human signal). Absent
  // storage (null) and stale ids (pcx_s rotated) never match.
  function isSecondLoad(storedValue, sessionId) {
    return !!sessionId && storedValue === sessionId;
  }

  // Batch assembly: split events into chunks bounded by BOTH max event count
  // and max serialized bytes (sizeOf measures one event; the collector
  // rejects-never-truncates over its byte cap, so the client must split).
  // A single event bigger than maxBytes ships alone in its own chunk (an
  // rrweb event cannot be split). An event whose sizeOf is null (it cannot
  // be serialized) is skipped in isolation — the caller logs it — so one bad
  // event never sinks a whole chunk of good ones. Order is preserved — the
  // FullSnapshot (type 2), first into the buffer, is first out in chunk 0.
  // When isFinal, only the LAST chunk is marked final; an empty final flush
  // yields one marker chunk {events: [], final: true} (caller substitutes the
  // marker event) so the terminal marker is never silently dropped.
  function assembleChunks(events, isFinal, maxEvents, maxBytes, sizeOf) {
    var chunks = [];
    var current = [];
    var currentBytes = 0;
    for (var i = 0; i < events.length; i++) {
      var size = sizeOf(events[i]);
      if (size === null) continue; // unserializable event: isolated drop, never sinks the chunk
      if (current.length > 0 &&
          (current.length >= maxEvents || currentBytes + size > maxBytes)) {
        chunks.push({ events: current, final: false });
        current = [];
        currentBytes = 0;
      }
      current.push(events[i]);
      currentBytes += size;
    }
    if (current.length > 0) chunks.push({ events: current, final: false });
    if (isFinal && chunks.length === 0) chunks.push({ events: [], final: false });
    if (isFinal) chunks[chunks.length - 1].final = true;
    return chunks;
  }

  // Pacing for non-final flushes: at most maxChunks POSTs per flush; events
  // beyond the cap are returned as overflow for the caller to RE-APPEND to
  // pending. Dropping overflow is silent data loss — every overflow event
  // must come back, in order.
  function applyFlushPacing(chunks, maxChunks) {
    if (chunks.length <= maxChunks) return { chunks: chunks, overflow: [] };
    var overflow = [];
    for (var i = maxChunks; i < chunks.length; i++) {
      overflow = overflow.concat(chunks[i].events);
    }
    return { chunks: chunks.slice(0, maxChunks), overflow: overflow };
  }

  // Byte-accounted keepalive budget: reserve on send, release on settle.
  // Without the release, settled requests never return capacity and the
  // final unload batch silently loses keepalive late in a long session.
  function makeKeepaliveBudget(quotaBytes) {
    var remaining = quotaBytes;
    return {
      remaining: function () { return remaining; },
      tryReserve: function (bytes) {
        if (bytes > remaining) return false;
        remaining -= bytes;
        return true;
      },
      release: function (bytes) { remaining += bytes; }
    };
  }

  // Overestimate of a batch body's UTF-8 bytes from its per-event sizes:
  // exact envelope bytes (caller measures them once) + event bytes + one byte
  // per event (covers the n-1 JSON array commas). Must never under-estimate:
  // the terminal-drain demotion decision fails unsafe if it does (a granted
  // batch that cannot actually reserve loses keepalive with no fallback).
  function estimateBatchBytes(eventSizes, envelopeBytes) {
    var total = envelopeBytes;
    for (var i = 0; i < eventSizes.length; i++) total += eventSizes[i] + 1;
    return total;
  }

  // Keepalive grant plan for a terminal drain: the final-marker batch gets
  // first claim on the budget (it is the one batch the budget exists to
  // protect — test-plan row 6), then earlier batches in seq order from what
  // remains. POST order is unchanged; only the grants are final-first.
  // Consuming the budget first-come would let earlier data chunks starve the
  // final-marker batch at exactly the moment it must survive unload.
  function planKeepaliveAllocation(byteSizes, finalIndex, budgetBytes) {
    var remaining = budgetBytes;
    var grants = new Array(byteSizes.length);
    var grantFinal = finalIndex >= 0 && finalIndex < byteSizes.length &&
        byteSizes[finalIndex] <= remaining;
    if (grantFinal) remaining -= byteSizes[finalIndex];
    for (var i = 0; i < byteSizes.length; i++) {
      if (i === finalIndex) {
        grants[i] = grantFinal;
      } else if (byteSizes[i] <= remaining) {
        grants[i] = true;
        remaining -= byteSizes[i];
      } else {
        grants[i] = false;
      }
    }
    return grants;
  }

  /* ─── PURE HELPERS END ─── */

  function readCookie(name) {
    return cookieFromString(document.cookie, name);
  }

  function generateUUID() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function warn(msg, e) {
    if (window.console && console.warn) console.warn('pcx-replay ' + msg, e || '');
  }

  function init() {
    // Vendored lib gate — assets/pcx-rrweb-record.min.js exposes
    // window.rrwebRecord.record (UMD; verified live 2026-06-06).
    var lib = window.rrwebRecord;
    var recordFn = lib && (typeof lib === 'function' ? lib : lib.record);
    if (typeof recordFn !== 'function') {
      warn('rrweb record lib missing — recorder inactive');
      return;
    }

    // Customer-account pages render PII (addresses, emails, order history)
    // as plain DOM text, outside maskAllInputs' reach — never record them.
    // NOTE: bare path-prefix check — a published second locale would move
    // these pages to /{locale}/account/... and slip past it. The structural,
    // locale-proof exclusion is the page_type guard in
    // snippets/custom-replay-recorder.liquid (these scripts never load on
    // customers/* pages); this client check is defense-in-depth only.
    if (location.pathname.indexOf('/account') === 0) return;

    // Identity comes from the tracker's cookies; bail if absent.
    var visitorId = readCookie(VISITOR_COOKIE);
    var sessionId = readCookie(SESSION_COOKIE);
    if (!visitorId || !sessionId) {
      warn('pcx_v/pcx_s cookies missing — recorder inactive (the inline entry block in custom-event-tracker.liquid mints them during head parse)');
      return;
    }

    var recordingId = generateUUID();      // one recording = one page-load
    var pagePath = location.pathname + location.search;

    var buffer = [];                       // pre-gate event buffer (capped)
    var pending = [];                      // post-gate, awaiting flush
    var transmitting = false;
    var disarmed = false;                  // pre-gate cap breached: recording abandoned
    var finalSent = false;                 // terminal latch: nothing transmits after final
    var seq = 0;
    var flushTimer = null;
    var maxScrollPct = 0;
    var keepaliveBudget = makeKeepaliveBudget(KEEPALIVE_QUOTA_BYTES);
    var stopFn = null;

    // Exact wire bytes of a batch payload MINUS its events — measured from
    // the real identity values so the terminal-drain estimate can never be
    // wrong about the envelope. +16 covers seq digit growth and the
    // max_scroll_pct null-vs-number variants.
    var envelopeBytes = utf8ByteLength(JSON.stringify({
      recording_id: recordingId,
      session_id: sessionId,
      visitor_id: visitorId,
      page_path: pagePath,
      seq: 0,
      events: [],
      final: true,
      max_scroll_pct: 100
    })) + 16;

    function eventSize(event) {
      try {
        return utf8ByteLength(JSON.stringify(event));
      } catch (e) {
        // null = unserializable: assembleChunks isolates the one bad event
        // instead of letting the whole-payload stringify sink a full chunk
        // of good events with no seq gap for the collector to notice.
        warn('unserializable event dropped', e);
        return null;
      }
    }

    // ─── transport ──────────────────────────────────────────────────────────
    function buildBatchPayload(events, isFinal) {
      var payload = {
        recording_id: recordingId,
        session_id: sessionId,
        visitor_id: visitorId,
        page_path: pagePath,
        seq: seq + 1,
        events: events,
        final: !!isFinal,
        max_scroll_pct: maxScrollPct === 0 ? null : maxScrollPct
      };
      var body;
      try {
        body = JSON.stringify(payload);
      } catch (e) {
        // seq NOT consumed — a batch that never left must not leave a gap.
        warn('serialize failed; batch dropped', e);
        return null;
      }
      seq += 1;
      return { body: body, seq: payload.seq, bytes: utf8ByteLength(body) };
    }

    function sendBatch(batch, wantKeepalive) {
      // keepalive quota is cumulative across in-flight requests, in bytes:
      // reserve from the budget on send, return it when the request settles.
      var reserved = !!wantKeepalive && keepaliveBudget.tryReserve(batch.bytes);
      function settle() {
        if (reserved) { keepaliveBudget.release(batch.bytes); reserved = false; }
      }
      try {
        fetch(COLLECT_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: batch.body,
          keepalive: reserved
        }).then(function (res) {
          settle();
          if (!res.ok) {
            // Collector rejects-never-truncates; a rejected batch is a
            // permanent seq gap — surface it, never swallow.
            warn('collector rejected batch seq=' + batch.seq + ' status=' + res.status);
          }
        }).catch(function (e) {
          settle();
          warn('network error seq=' + batch.seq, e);
        });
      } catch (e) {
        settle();
        warn('post failed', e);
      }
    }

    function postBatch(events, isFinal, wantKeepalive) {
      var batch = buildBatchPayload(events, isFinal);
      if (batch) sendBatch(batch, wantKeepalive);
    }

    // isFinal: terminal flush — drain everything, mark the last chunk final
    // (or send a marker-only batch when nothing is pending), latch finalSent.
    // wantKeepalive: unload-path flushes request keepalive within the budget.
    function flush(isFinal, wantKeepalive) {
      if (!transmitting || finalSent) return;
      if (pending.length === 0) {
        if (!isFinal) return;
        if (seq === 0) { finalSent = true; return; } // nothing ever sent — nothing to terminate
      }
      var chunks = assembleChunks(pending, !!isFinal, BATCH_MAX_EVENTS, BATCH_MAX_BYTES, eventSize);
      pending = [];
      if (!isFinal) {
        // Pacing: cap the synchronous POST burst (gate-open on a long-
        // buffered tab); overflow events return to pending — every one of
        // them — for the interval / next flush.
        var paced = applyFlushPacing(chunks, MAX_CHUNKS_PER_FLUSH);
        pending = paced.overflow;
        chunks = paced.chunks;
        for (var i = 0; i < chunks.length; i++) {
          postBatch(chunks[i].events, false, wantKeepalive);
        }
        return;
      }
      // ─── terminal drain ───
      // Substitute the marker into an idle (empty) final chunk up front so
      // the byte arithmetic below measures what is actually posted.
      for (var j = 0; j < chunks.length; j++) {
        chunks[j].events = substituteMarkerEvents(chunks[j].events, makeFinalMarkerEvent(Date.now()));
      }
      // The final marker must ride keepalive — a plain fetch issued during
      // pagehide is cancellable with the document, and its .catch never runs
      // on a destroyed page (silent, permanent ingest_complete loss). If the
      // final data chunk cannot fit the budget, demote it and append a
      // marker-only final batch (~300 B) that can. The over-budget data is
      // lost either way (inherent keepalive quota); the terminal marker is not.
      if (wantKeepalive) {
        var lastChunk = chunks[chunks.length - 1];
        var lastSizes = [];
        for (var s = 0; s < lastChunk.events.length; s++) {
          var sz = eventSize(lastChunk.events[s]);
          lastSizes.push(sz === null ? 0 : sz);
        }
        if (estimateBatchBytes(lastSizes, envelopeBytes) > keepaliveBudget.remaining()) {
          lastChunk.final = false;
          chunks.push({ events: [makeFinalMarkerEvent(Date.now())], final: true });
        }
      }
      // Build every payload in seq order, then grant keepalive FINAL-FIRST
      // (the budget would otherwise be consumed FIFO and the final-marker
      // batch — last in line — starved at exactly the wrong moment).
      var batches = [];
      var finalIdx = -1;
      for (var k = 0; k < chunks.length; k++) {
        var built = buildBatchPayload(chunks[k].events, chunks[k].final);
        if (!built) continue;
        if (chunks[k].final) finalIdx = batches.length;
        batches.push(built);
      }
      var grants = planKeepaliveAllocation(
        batches.map(function (b) { return b.bytes; }),
        finalIdx,
        wantKeepalive ? keepaliveBudget.remaining() : 0
      );
      for (var m = 0; m < batches.length; m++) {
        sendBatch(batches[m], grants[m]);
      }
      finalSent = true;
    }

    function openGate() {
      if (transmitting || disarmed || finalSent) return;
      transmitting = true;
      removeGateListeners();
      // Move the whole buffer (initial FullSnapshot included) to pending and
      // flush immediately — the first transmitted chunk carries the snapshot.
      pending = buffer;
      buffer = [];
      flush(false, false);
      flushTimer = setInterval(function () { flush(false, false); }, BATCH_INTERVAL_MS);
    }

    function teardown() {
      if (flushTimer) { clearInterval(flushTimer); flushTimer = null; }
      if (stopFn) {
        try { stopFn(); } catch (e) { /* rrweb already stopped */ }
        stopFn = null;
      }
      removeGateListeners();
      // The lifecycle/summary listeners do real work for the page's whole
      // remaining lifetime (rAF scroll maths) — detach them too once the
      // recording is over for good (disarm, debug stop, or post-finalize).
      window.removeEventListener('scroll', onScrollDepth);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
    }

    // Pre-gate cap breached with no human signal: a replay missing its early
    // events (the FullSnapshot) is unreplayable, so partial dropping is never
    // safe — discard the whole recording and disarm. The no-signal population
    // is discard-by-design anyway; this just bounds its memory cost.
    function disarm() {
      disarmed = true;
      buffer = [];
      teardown();
      warn('pre-gate buffer exceeded ' + PRE_GATE_BUFFER_MAX_EVENTS + ' events with no human signal; recording disarmed');
    }

    // Terminal: deliver everything with the final marker, then stop for good.
    // Fired by pagehide ONLY — visibilitychange→hidden is not terminal (tab
    // switches must not end the recording); it just hurries batches out.
    function finalize() {
      try {
        if (transmitting && !finalSent) flush(true, true);
      } catch (e) { warn('final flush error', e); }
      teardown();
      buffer = [];   // gate never opened → bot path transmits nothing, by design
    }

    // ─── rrweb ──────────────────────────────────────────────────────────────
    try {
      stopFn = recordFn({
        emit: function (event) {
          try {
            if (disarmed || finalSent) return;
            if (transmitting) {
              pending.push(event);
              if (pending.length >= BATCH_MAX_EVENTS) flush(false, false);
            } else {
              buffer.push(event);
              if (buffer.length > PRE_GATE_BUFFER_MAX_EVENTS) disarm();
            }
          } catch (e) { warn('emit error', e); }
        },
        maskAllInputs: true,
        recordCanvas: false,
        // Defense-in-depth on top of maskAllInputs: password fields and the
        // theme's newsletter email inputs are blocked outright (never
        // serialized). The footer input is server-side pre-filled with the
        // logged-in customer's email, so masking alone is not sufficient.
        // Selectors verified against the live theme markup (and pinned to it
        // by the liquid-coupling tests in tests/pcx-replay-recorder.test.mjs):
        //   snippets/custom-inline-newsletter.liquid → .pc-inline-nl__input
        //     (also rendered by sections/custom-newsletter-page.liquid)
        //   snippets/custom-newsletter-popup.liquid  → .pc-nl-input
        //   snippets/footer-newsletter.liquid        → .footer__newsletter-input
        blockSelector: 'input[type="password"], .pc-inline-nl__input, .pc-nl-input, .footer__newsletter-input'
      });
    } catch (e) {
      warn('record init failed', e);
      return;
    }

    // ─── human-signal gate ──────────────────────────────────────────────────
    // All handlers ignore synthetic events (isTrusted === false): a script-
    // dispatched scroll/click is not a human signal. Trusted browser-initiated
    // scrolls (fragment navigation, scroll restoration) still pass — accepted
    // residual; the server-side classifier stays flag-not-drop regardless.
    var firstMove = null;
    function onPointerMove(evt) {
      try {
        if (evt.isTrusted === false) return;
        if (firstMove === null) {
          firstMove = { x: evt.clientX, y: evt.clientY };
          return;
        }
        if (moveExceedsGate(firstMove.x, firstMove.y, evt.clientX, evt.clientY, MOVE_GATE_PX)) {
          openGate();
        }
      } catch (e) { warn('gate error', e); }
    }
    function onScroll(evt) { if (evt.isTrusted === false) return; openGate(); }
    function onPointerDown(evt) { if (evt.isTrusted === false) return; openGate(); }
    function onKeyDown(evt) { if (evt.isTrusted === false) return; openGate(); }

    function addGateListeners() {
      document.addEventListener('pointermove', onPointerMove, { capture: true, passive: true });
      window.addEventListener('scroll', onScroll, { capture: true, passive: true });
      document.addEventListener('pointerdown', onPointerDown, { capture: true, passive: true });
      document.addEventListener('keydown', onKeyDown, { capture: true, passive: true });
    }
    function removeGateListeners() {
      document.removeEventListener('pointermove', onPointerMove, { capture: true });
      window.removeEventListener('scroll', onScroll, { capture: true });
      document.removeEventListener('pointerdown', onPointerDown, { capture: true });
      document.removeEventListener('keydown', onKeyDown, { capture: true });
    }

    // Second page-load in the same pcx_s session opens the gate immediately
    // (multi-page navigation is itself the human signal). localStorage, NOT
    // sessionStorage: the AC scopes this to the pcx_s session, which spans
    // tabs — a product opened in a new tab is the same session. Stale entries
    // self-invalidate because pcx_s rotates: an old stored id can never equal
    // the current session's (isSecondLoad pins the comparison direction).
    var secondLoad = false;
    try {
      secondLoad = isSecondLoad(localStorage.getItem(SEEN_KEY), sessionId);
      localStorage.setItem(SEEN_KEY, sessionId);
    } catch (e) { /* privacy modes: gate stays interaction-only */ }

    if (secondLoad) {
      openGate();
    } else {
      addGateListeners();
    }

    // ─── scroll depth (summary signal, mirrors pcx-tracker maths) ──────────
    var scrollTicking = false;
    function onScrollDepth() {
      if (scrollTicking) return;
      scrollTicking = true;
      requestAnimationFrame(function () {
        scrollTicking = false;
        try {
          var doc = document.documentElement;
          var scrollTop = window.scrollY || doc.scrollTop || 0;
          var winHeight = window.innerHeight || doc.clientHeight || 0;
          var docHeight = Math.max(
            document.body.scrollHeight, doc.scrollHeight,
            document.body.offsetHeight, doc.offsetHeight, doc.clientHeight
          );
          var pct = scrollDepthPct(scrollTop, winHeight, docHeight);
          if (pct > maxScrollPct) maxScrollPct = pct;
        } catch (e) { /* ignore */ }
      });
    }
    window.addEventListener('scroll', onScrollDepth, { passive: true });

    // ─── unload delivery / discard ──────────────────────────────────────────
    // pagehide = real leave (navigation, close, bfcache entry) → finalize.
    function onPageHide() {
      try { finalize(); } catch (e) { warn('unload error', e); }
    }
    window.addEventListener('pagehide', onPageHide);
    // Tab-hide is NOT terminal — the user usually comes back (mobile tab
    // switching). Hurry pending batches out with keepalive in case the tab is
    // later killed without pagehide; this is a NON-final flush, so the pacing
    // cap applies and a >6-chunk backlog leaves its tail in pending for the
    // next flush (accepted edge — emit-side flushing keeps pending small).
    // Recording continues and no final marker is sent; pagehide owns the
    // terminal marker.
    function onVisibilityChange() {
      try {
        if (document.visibilityState === 'hidden') flush(false, true);
      } catch (e) { warn('hidden flush error', e); }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Debug handle (mirrors window.PeterCox.tracker).
    window.PeterCox = window.PeterCox || {};
    window.PeterCox.replay = {
      _recordingId: recordingId,
      _isTransmitting: function () { return transmitting; },
      _bufferLength: function () { return buffer.length + pending.length; },
      _openGate: openGate,
      _stop: teardown
    };
  }

  try {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        try { init(); } catch (e) { warn('init failed', e); }
      });
    } else {
      init();
    }
  } catch (e) {
    warn('bootstrap failed', e);
  }
})();
